/**
 * Resolve the current PDP's platform product id, across every storefront Pilcha
 * supports. Extracted from loader.ts so the priority order is unit-testable in
 * isolation (importing loader.ts would run its bootstrap side-effects).
 *
 * Priority matters: platform-specific, PDP-scoped signals (VTEX `data-vtex-
 * product-id`, `__RUNTIME__`, Shopify `ShopifyAnalytics`, Tiendanube
 * `LS.product.id`, Woo/Magento) come BEFORE the generic `[data-product-id]`
 * fallback. That generic attribute is stamped on related-product / carousel
 * cards by many themes, so a document-wide match can return a DIFFERENT product
 * than the one on the page — which made the widget open the wrong product on a
 * Nuvemshop PDP that listed a stale test product as "related".
 */
export function detectProductId(): string | null {
  const meta = document.querySelector('meta[property="product:id"]') as HTMLMetaElement | null;
  if (meta?.content) return meta.content;

  // VTEX-specific attribute is canonical for the PDP's main product. The
  // GENERIC `[data-product-id]` is intentionally NOT matched here (see the
  // module doc): it's a last-resort fallback at the END of this function,
  // after every platform global.
  const vtexAttr = document.querySelector('[data-vtex-product-id]');
  const vtexId = vtexAttr?.getAttribute('data-vtex-product-id');
  if (vtexId) return vtexId;

  const w = window as unknown as {
    __RUNTIME__?: { product?: { productId?: string } };
    ShopifyAnalytics?: { meta?: { product?: { id?: string | number } } };
    LS?: { product?: { id?: string | number } };
    wc_single_product_params?: { product_id?: string | number };
  };

  // VTEX IO. spec 004
  if (w.__RUNTIME__?.product?.productId) return w.__RUNTIME__.product.productId;

  // Shopify. ShopifyAnalytics.meta.product is injected on PDPs only. If it
  // isn't there we're almost certainly not on a product page; the caller
  // handles null by skipping injection, so treat absence as "not a PDP".
  const shopifyId = w.ShopifyAnalytics?.meta?.product?.id;
  if (shopifyId != null) return String(shopifyId);

  // Tiendanube. `LS.product.id` is only populated on PDP routes. Their
  // themes don't emit the `meta property="product:id"` tag by default, so
  // this global is the canonical source.
  const tiendanubeId = w.LS?.product?.id;
  if (tiendanubeId != null) return String(tiendanubeId);

  // WooCommerce. spec 023. `body.single-product` is always present on the
  // PDP; the product id comes from either the global set by default themes
  // or the add-to-cart form.
  if (document.body.classList.contains('single-product')) {
    const wcId = w.wc_single_product_params?.product_id;
    if (wcId != null) return String(wcId);
    // The add-to-cart control carries the product id in its `value`. Simple
    // products (Storefront + most themes) render it as a `<button name="add-to-cart"
    // value="ID">`; variable/legacy themes use `<input name="add-to-cart">` or a
    // hidden `<input name="product_id">`. Cover all three.
    const atc = document.querySelector(
      'form.cart button[name="add-to-cart"], form.cart input[name="add-to-cart"], form.cart input[name="product_id"]',
    ) as HTMLButtonElement | HTMLInputElement | null;
    if (atc?.value) return atc.value;
    // Last resort: the `postid-<ID>` class WooCommerce always puts on <body>.
    const cls = Array.from(document.body.classList).find((c) => /^postid-\d+$/.test(c));
    if (cls) return cls.replace('postid-', '');
  }

  // Magento 2. spec 023. Every PDP has `body.catalog-product-view`; the
  // numeric id is published as `<meta itemprop="productID">` by the default
  // theme and as a data attribute on the add-to-cart form.
  if (document.body.classList.contains('catalog-product-view')) {
    const metaProd = document.querySelector(
      'meta[itemprop="productID"]',
    ) as HTMLMetaElement | null;
    if (metaProd?.content) return metaProd.content;
    const addToCart = document.querySelector('#product_addtocart_form');
    const magentoId = addToCart?.getAttribute('data-product-id');
    if (magentoId) return magentoId;
  }

  // Last resort ONLY: a generic `data-product-id` anywhere in the DOM. Unreliable
  // (themes stamp it on related-product cards too), so it runs AFTER every
  // platform-specific signal above — a real PDP almost always resolved already.
  const generic = document.querySelector('[data-product-id]');
  const genericId = generic?.getAttribute('data-product-id');
  if (genericId) return genericId;

  return null;
}
